import { Alert, App, List, Modal, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { archiveProject } from "../api/services.ts"
import type { PendingCommitment } from "../api/services.types.ts"
import { PROJECTS_SAVE_RESULT } from "../constants/projects-save-results.ts"

const { Text } = Typography

interface ArchiveProjectModalProps {
  projectId: string
  projectName?: string | null
  open: boolean
  onCancel: () => void
  onArchived?: () => void
}

export function ArchiveProjectModal({
  projectId,
  projectName,
  open,
  onCancel,
  onArchived
}: ArchiveProjectModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { formatDate } = useDateFormatter()
  const [saving, setSaving] = useState(false)
  // Set when the server refuses because the project still has meetings booked.
  const [commitments, setCommitments] = useState<PendingCommitment[] | null>(
    null
  )

  const archive = async (cancelAppointments: boolean) => {
    setSaving(true)
    const response = await archiveProject({
      id: projectId,
      ...(cancelAppointments ? { cancel_appointments: true } : {})
    })
    setSaving(false)

    if (response.ok) {
      setCommitments(null)
      message.success(t("projectArchive.success"))
      onArchived?.()
      return
    }

    const details = response.data as
      | {
          type?: string
          message?: string
          detail?: string
          pending_commitments?: PendingCommitment[]
        }
      | undefined

    if (details?.type !== PROJECTS_SAVE_RESULT.PENDING_COMMITMENTS) {
      // Leaving confirm mode up would pin the modal to a meeting list that no
      // longer describes the state and keep re-sending the confirmation.
      setCommitments(null)
      message.error(details?.message || details?.detail || t("projectArchive.failed"))
      return
    }

    const listed = details.pending_commitments ?? []
    if (!listed.length) {
      setCommitments(null)
      message.warning(
        details.message || t("projectArchive.commitments.unavailable")
      )
      return
    }

    setCommitments(listed)
  }

  const displayName = projectName?.trim() || t("projectArchive.thisProject")
  const hasCommitments = !!commitments?.length

  return (
    <Modal
      open={open}
      title={t("projectArchive.title", { name: displayName })}
      okText={
        hasCommitments
          ? t("projectArchive.commitments.confirm")
          : t("projectArchive.confirm")
      }
      cancelText={
        hasCommitments
          ? t("projectArchive.commitments.keepActive")
          : t("projectArchive.cancel")
      }
      okButtonProps={{ danger: true }}
      confirmLoading={saving}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      keyboard={!saving}
      maskClosable={false}
      onOk={() => archive(hasCommitments)}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Alert
        type="warning"
        showIcon
        className="mb-4"
        message={t("projectArchive.impact")}
      />

      {hasCommitments && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          data-testid="archive-pending-commitments"
          message={t("projectArchive.commitments.title")}
          description={
            <>
              <div className="mb-2">
                {t("projectArchive.commitments.body", {
                  count: commitments?.length ?? 0
                })}
              </div>
              <List
                size="small"
                dataSource={commitments ?? []}
                renderItem={(commitment) => (
                  <List.Item className="!px-0">
                    <div className="flex flex-col">
                      <Text strong>{commitment.title}</Text>
                      <Text type="secondary" className="text-xs">
                        {formatDate(commitment.start_at)}
                        {commitment.has_external_invitees
                          ? ` · ${t("projectArchive.commitments.externalInvitee")}`
                          : ""}
                      </Text>
                    </div>
                  </List.Item>
                )}
              />
            </>
          }
        />
      )}

    </Modal>
  )
}
